import { useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Download, ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import { useAdminYouTubeDownloadsQuery, useAdminStatsQuery } from "@/generated/graphql";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import type { ChartConfig } from "@/components/ui/chart";
import { formatDateTime } from "@/lib/format";

type SortField = "video_id" | "download_status" | "provider" | "language" | "priority" | "linked_projects_count" | "createdAt";

const AdminDownloads = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const pageSize = 20;

  const { data, loading, error } = useAdminYouTubeDownloadsQuery({
    variables: {
      page,
      pageSize,
      search: search || undefined,
      sortBy,
      sortOrder,
    },
    fetchPolicy: "cache-and-network",
  });

  const { data: statsData } = useAdminStatsQuery();

  const chartConfig = {
    total: { label: t("admin.common.chartTotal"), color: "hsl(var(--primary))" },
    success: { label: t("admin.common.chartSuccess"), color: "hsl(142 76% 36%)" },
    failure: { label: t("admin.common.chartFailure"), color: "hsl(0 84% 60%)" },
  } satisfies ChartConfig;

  const timeSeries = statsData?.adminStats?.youtubeDownloads?.timeSeries || [];

  const downloads = data?.adminYouTubeDownloads?.data || [];
  const totalPages = data?.adminYouTubeDownloads?.pageCount || 1;
  const total = data?.adminYouTubeDownloads?.total || 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return null;
    return sortOrder === "asc" ? (
      <ArrowUp className="w-3 h-3 ml-1 inline" />
    ) : (
      <ArrowDown className="w-3 h-3 ml-1 inline" />
    );
  };

  const getStatusColor = (status: string | null | undefined) => {
    switch (status) {
      case "completed":
        return "bg-green-500/10 text-green-500 border-green-500";
      case "downloading":
      case "processing":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500";
      case "failed":
        return "bg-red-500/10 text-red-500 border-red-500";
      case "pending":
        return "bg-blue-500/10 text-blue-500 border-blue-500";
      default:
        return "bg-gray-500/10 text-gray-500 border-gray-500";
    }
  };

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Download className="w-8 h-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">{t('admin.downloads.title')}</h1>
                <p className="text-muted-foreground">{t('admin.downloads.subtitle')}</p>
              </div>
            </div>
            <Badge variant="outline" className="text-red-500 border-red-500">
              ADMIN
            </Badge>
          </div>

          {timeSeries.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.downloads.dailyChart')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px] w-full">
                  <BarChart data={timeSeries}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(v) => new Date(v + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis tickLine={false} axisLine={false} allowDecimals={false} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="total" fill="var(--color-total)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="success" fill="var(--color-success)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="failure" fill="var(--color-failure)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('admin.downloads.list')}</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {t('admin.common.total')}: {total}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-2 mb-4">
                <Input
                  placeholder={t('admin.downloads.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="max-w-sm"
                />
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
              </form>

              {loading && !data ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500">
                  {t('admin.common.error')}
                </div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("video_id")}>
                            {t('admin.downloads.videoId')}<SortIcon field="video_id" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("download_status")}>
                            {t('admin.downloads.status')}<SortIcon field="download_status" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("provider")}>
                            {t('admin.downloads.provider')}<SortIcon field="provider" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("language")}>
                            {t('admin.downloads.language')}<SortIcon field="language" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("priority")}>
                            {t('admin.downloads.priority')}<SortIcon field="priority" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("linked_projects_count")}>
                            {t('admin.downloads.linkedProjects')}<SortIcon field="linked_projects_count" />
                          </TableHead>
                          <TableHead className="cursor-pointer select-none" onClick={() => handleSort("createdAt")}>
                            {t('admin.downloads.createdAt')}<SortIcon field="createdAt" />
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {downloads.map((download) => (
                          <TableRow key={download.id}>
                            <TableCell>
                              <a
                                href={`https://www.youtube.com/watch?v=${download.video_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline"
                              >
                                {download.video_id}
                              </a>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={getStatusColor(download.download_status)}>
                                {download.download_status}
                              </Badge>
                            </TableCell>
                            <TableCell>{download.provider || "-"}</TableCell>
                            <TableCell>{download.language || "-"}</TableCell>
                            <TableCell>{download.priority ?? "-"}</TableCell>
                            <TableCell>{download.linked_projects_count}</TableCell>
                            <TableCell>{formatDateTime(download.createdAt)}</TableCell>
                          </TableRow>
                        ))}
                        {downloads.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                              {t('admin.common.noResults')}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-muted-foreground">
                      {t('admin.common.page')} {page} {t('admin.common.of')} {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </Layout>
  );
};

export default AdminDownloads;
